/**
 * Copyright (c) 2022 Animation Studio Voronezh.
 *
 * CONFIDENTIAL AND PROPRIETARY
 *
 * This work is provided "AS IS" and subject to the Prodtrack
 * Source Code License. By accessing, using, copying or modifying
 * this work you indicate your agreement to the Prodtrack Source Code License.
 * All rights not expressly granted therein are reserved by Animation Studio Voronezh.
 */

import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import Login from "./routing/Login";
import * as Sentry from "@sentry/react";
import ErrorBoundaryFallback from "./routing/ErrorBoundaryFallback";
import ThemeContextProvider from "./contexts/ThemeContextProvider";
import store from "./redux";

import "semantic-ui-css/semantic.min.css";
import "./index.css";
import CurrentContextProvider from "contexts/CurrentContextProvider";
import TableViewGanttOptionsContextProvider from "contexts/TableViewGanttOptionsContextProvider";
import BoardViewContextProvider from "contexts/BoardViewContextProvider";
import PipelineStepModalContextProvider from "contexts/PipelineStepModalContextProvider";
import TaskTemplateModalContextProvider from "contexts/TaskTemplateModalContextProvider";
import SelectedRowsContextProvider from "contexts/SelectedRowsContextProvider";
import CeleryProgressContextProvider from "contexts/CeleryProgressContextProvider";

if (
    process.env.NODE_ENV === "production" &&
    window.SENTRY_DSN_FRONTEND &&
    window.SENTRY_DSN_FRONTEND != ""
) {
    Sentry.init({
        dsn: window.SENTRY_DSN_FRONTEND,
        integrations: [
            Sentry.httpClientIntegration(),
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        sendDefaultPii: true,
        release: process.env.REACT_APP_PKG_VERSION,
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        profilesSampleRate: 1.0,
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            "localhost",
            /^https:\/\/prodtrack\.voronezh\.studio\/api\/v1/,
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

ReactDOM.render(
    <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => {
            Sentry.captureException(error);
            return (
                <ErrorBoundaryFallback error={error} resetError={resetError} />
            );
        }}
    >
        <Provider store={store}>
            <CeleryProgressContextProvider>
                <ThemeContextProvider>
                    <CurrentContextProvider>
                        <SelectedRowsContextProvider>
                            <TableViewGanttOptionsContextProvider>
                                <BoardViewContextProvider>
                                    <PipelineStepModalContextProvider>
                                        <TaskTemplateModalContextProvider>
                                            <BrowserRouter>
                                                <Login />
                                            </BrowserRouter>
                                        </TaskTemplateModalContextProvider>
                                    </PipelineStepModalContextProvider>
                                </BoardViewContextProvider>
                            </TableViewGanttOptionsContextProvider>
                        </SelectedRowsContextProvider>
                    </CurrentContextProvider>
                </ThemeContextProvider>
            </CeleryProgressContextProvider>
        </Provider>
    </Sentry.ErrorBoundary>,
    document.getElementById("root")
);
